import { Base, withBreakpointObserver } from '@studiometa/js-toolkit';
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

/**
 * Slider component
 */
export default class Slider extends withBreakpointObserver(Base) {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Slider',
    refs: ['slider', 'prev', 'next', 'pagination'],
    options: {
      spaceBetween: {
        type: Number,
        default: 0,
      },
      // Espace entre les slides sous le breakpoint `s` (640px). Retombe sur
      // `spaceBetween` si non défini.
      spaceBetweenMobile: {
        type: Number,
        default: 0,
      },
      slidesPerView: {
        type: Number,
        default: 0,
      },
      // Nombre de slides visibles sur mobile (< 480px), peek possible (ex : 2.5).
      slidesPerViewMobile: {
        type: Number,
        default: 0,
      },
      loop: {
        type: Boolean,
        default: false,
      },
      centered: {
        type: Boolean,
        default: false,
      },
      // Force un seul slide visible à tous les breakpoints (ex : avis clients).
      single: {
        type: Boolean,
        default: false,
      },
      // Défilement automatique (ex : feed Instagram).
      autoplay: {
        type: Boolean,
        default: false,
      },
    },
  };

  /**
   *
   */
  mounted() {
    const { single, slidesPerViewMobile, spaceBetween, spaceBetweenMobile } = this.$options;
    const mobileSlidesPerView = slidesPerViewMobile > 0 ? slidesPerViewMobile : 'auto';
    const mobileSpaceBetween = spaceBetweenMobile > 0 ? spaceBetweenMobile : spaceBetween;

    this.slider = new Swiper(this.$refs.slider, {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: single ? 1 : mobileSlidesPerView,
      grabCursor: true,
      loop: this.$options.loop,
      centeredSlides: this.$options.centered,
      // Centre les slides lorsqu'il n'y en a pas assez pour remplir la vue.
      centerInsufficientSlides: true,
      spaceBetween: mobileSpaceBetween,
      autoplay: this.$options.autoplay
        ? {
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
      navigation: {
        nextEl: this.$refs.next,
        prevEl: this.$refs.prev,
      },
      // Pagination en dots, activée uniquement si un ref `pagination` est présent.
      pagination: this.$refs.pagination
        ? {
            el: this.$refs.pagination,
            clickable: true,
          }
        : false,
      breakpoints: single
        ? {}
        : {
            480: {
              slidesPerView: 2, // Feel free to change this value !
              centeredSlides: false,
            },
            640: {
              slidesPerView: 3, // Feel free to change this value !
              centeredSlides: false,
              spaceBetween,
            },
            1024: {
              slidesPerView: this.$options.slidesPerView > 0 ? this.$options.slidesPerView : 'auto',
              centeredSlides: false,
              spaceBetween,
            },
          },
      on: {
        init: () => this.setMediaHeight(),
        resize: () => this.setMediaHeight(),
      },
    });
  }

  /**
   * Détruit l'instance Swiper lorsque le composant est démonté (ex : passage
   * à un breakpoint inactif via `data-option-active-breakpoints`).
   */
  destroyed() {
    this.slider?.destroy(true, true);
    this.slider = null;
  }

  /**
   * Expose the media (image) height so the navigation arrows can be
   * vertically centered on the image instead of the whole card.
   */
  setMediaHeight() {
    const media = this.$refs.slider.querySelector('.swiper-slide .aspect-square');

    if (media) {
      this.$el.style.setProperty('--slider-media-h', `${media.offsetHeight}px`);
    }
  }
}
